import React from 'react';
import { PiggyBank, Lock, Truck, Shield, TrendingUp } from 'lucide-react';
import { formatMoney } from '../../utils/format';
import { useTranslation } from '../../i18n';
import { localize } from '../../data/i18nData';
import { BANKS, getLoyaltyLevel, getAvailablePlacements, getBankSeniorityMonths, LOYALTY_LABELS } from '../../data/bankData';

const PLACEMENT_ICON_MAP = { piggy: PiggyBank, lock: Lock, truck: Truck, shield: Shield, leaf: TrendingUp };

export function PlacementsTab({ activePlacements, bankRelations, treasury, placementAmount, setPlacementAmount, subscribePlacement, withdrawPlacement, totalDays }) {
  const { t } = useTranslation();
  return (
    <div className="space-y-4">
      {activePlacements.length > 0 && (
        <div className="bg-ink-800 rounded-lg border border-ink-600 p-4">
          <h3 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2">
            <PiggyBank className="w-4 h-4" /> Mes placements actifs
          </h3>
          <div className="space-y-2">
            {activePlacements.map(p => {
              const pBank = BANKS[p.bankId];
              const monthsElapsed = Math.floor(((totalDays || 0) - (p.startDay || 0)) / 30);
              const monthsLeft = Math.max(0, (p.term || 0) - monthsElapsed);
              const mature = p.liquid || monthsLeft <= 0;
              return (
                <div key={p.id} className="flex items-center justify-between p-3 rounded-lg border border-ink-600 bg-ink-700/30">
                  <div className="flex items-center gap-2">
                    {pBank && <img src={pBank.advisor.portrait} alt="" className="w-7 h-7 rounded-full object-cover object-top" />}
                    <div>
                      <p className="text-xs font-medium text-slate-200">{p.name}</p>
                      <p className="text-[10px] text-slate-500">
                        {formatMoney(p.amount)} à {(p.rate * 100).toFixed(1)}% — {formatMoney(p.monthlyInterest)}/mois
                        {p.term > 0 && (mature
                          ? ' — '
                          : ` — encore ${monthsLeft} mois`)}
                        {mature && p.term > 0 && (
                          <span className="text-money font-bold">arrivé à maturité ✓</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => withdrawPlacement(p.id)}
                    disabled={!mature}
                    className={`text-xs px-3 py-1.5 rounded-md disabled:opacity-40 ${
                      mature
                        ? 'border border-money/40 text-money hover:bg-money/10 font-semibold'
                        : 'border border-danger/40 text-danger hover:bg-danger/10'
                    }`}
                    data-testid={`withdraw-${p.id}`}
                    title={mature ? 'Récupérer les fonds' : `Bloqué encore ${monthsLeft} mois`}
                  >
                    {mature ? 'Récupérer' : `Bloqué (${monthsLeft}m)`}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {Object.values(BANKS).map(rawB => {
        const b = localize('bank', rawB);
        const bRel = bankRelations[b.id] || { loansRepaid: 0 };
        const seniorityMonths = getBankSeniorityMonths(bRel, totalDays);
        const bLoyalty = getLoyaltyLevel(b.id, bRel.loansRepaid, seniorityMonths || 0);
        const allProducts = getAvailablePlacements(b.id, { treasury, loyaltyLevel: bLoyalty });
        const hasUnlocked = allProducts.some(p => p.unlocked);

        return (
          <div key={b.id} className="bg-ink-800 rounded-lg border border-ink-600 overflow-hidden">
            <div className="flex items-center gap-3 p-3 border-b border-ink-600" style={{ background: `${b.color}08` }}>
              <img src={b.advisor.portrait} alt="" className="w-9 h-9 rounded-full object-cover object-top border-2" style={{ borderColor: b.color }} />
              <div className="flex-1">
                <p className="text-sm font-bold" style={{ color: b.color }}>{b.name}</p>
                <p className="text-[10px] text-slate-500">{b.advisor.name} — {allProducts.filter(p => p.unlocked).length}/{allProducts.length} {t('products_available')}</p>
                <p className="text-[10px] mt-0.5 flex items-center gap-1.5" data-testid={`bank-seniority-${b.id}`}>
                  <span className="px-1.5 py-0.5 rounded font-mono font-bold" style={{ background: `${b.color}20`, color: b.color }}>
                    {LOYALTY_LABELS[bLoyalty]}
                  </span>
                  {seniorityMonths !== null ? (
                    <span className="text-slate-500">
                      Client depuis {seniorityMonths >= 12
                        ? `${Math.floor(seniorityMonths / 12)} an${seniorityMonths >= 24 ? 's' : ''}${seniorityMonths % 12 ? ` ${seniorityMonths % 12} mois` : ''}`
                        : `${seniorityMonths} mois`}
                    </span>
                  ) : (
                    <span className="text-slate-600 italic">Prospect</span>
                  )}
                </p>
              </div>
            </div>
            {hasUnlocked && (
              <p className="px-3 pt-2 text-xs text-slate-400 italic">"{b.advisor.placementOffer[0]}"</p>
            )}
            <div className="p-3 space-y-2">
              {allProducts.map(product => {
                const IconComp = PLACEMENT_ICON_MAP[product.icon] || PiggyBank;
                return (
                  <div key={product.id} className={`rounded-lg border p-3 ${product.unlocked ? 'border-ink-600' : 'border-ink-600 opacity-50'}`}>
                    <div className="flex items-start justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: product.unlocked ? b.color + '15' : '#F3F4F6' }}>
                          <IconComp className="w-3.5 h-3.5" style={{ color: product.unlocked ? b.color : '#9CA3AF' }} />
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-slate-200">{product.name}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-[10px] font-bold" style={{ color: product.unlocked ? b.color : '#9CA3AF' }}>{(product.rate * 100).toFixed(1)}%</span>
                            {product.termMonths > 0 && <span className="text-[9px] px-1 py-0.5 rounded bg-ink-700/40 text-slate-500">Bloqué {product.termMonths} mois</span>}
                            {product.liquid && <span className="text-[9px] px-1 py-0.5 rounded bg-money/10 text-money">Disponible</span>}
                            {product.taxBonus && <span className="text-[9px] px-1 py-0.5 rounded bg-accent/10 text-accent">IS -{(product.taxBonus * 100)}%</span>}
                          </div>
                        </div>
                      </div>
                      <span className="text-[9px] text-slate-500">min. {formatMoney(product.minAmount)}</span>
                    </div>
                    <p className="text-[10px] text-slate-500 mb-2">{product.description}</p>
                    {product.unlocked ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          min={product.minAmount}
                          max={product.maxAmount}
                          step={1000}
                          value={Math.min(Math.max(placementAmount, product.minAmount), product.maxAmount)}
                          onChange={e => setPlacementAmount(Number(e.target.value))}
                          className="flex-1 accent-[#1F3864]"
                        />
                        <span className="text-[10px] font-bold text-slate-300 w-16 text-right">{formatMoney(placementAmount)}</span>
                        <button
                          onClick={() => subscribePlacement(b.id, placementAmount, product)}
                          disabled={treasury < placementAmount || placementAmount < product.minAmount}
                          className="text-xs text-foreground px-3 py-1.5 rounded-md font-medium hover:opacity-90 disabled:opacity-40"
                          style={{ backgroundColor: b.color }}
                          data-testid={`subscribe-${product.id}`}
                        >
                          {t('invest')}
                        </button>
                      </div>
                    ) : (
                      <p className="text-[10px] text-danger flex items-center gap-1"><Lock className="w-3 h-3" /> {product.lockReason}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
